/**
 * Curves Visualization Modal - Display FlowMatch reference curves in an interactive chart
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  Paper,
  IconButton,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Restore as RestoreIcon,
  SaveAlt as SaveAltIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useTheme } from '../../../contexts/ThemeContext';
import { DetectionProfile, normalizeSignatureData } from '../../../types/cardReaderProfiles';

interface CurvesVisualizationModalProps {
  open: boolean;
  onClose: () => void;
  profile: DetectionProfile | null;
}

interface CanvasState {
  scale: number;
  offsetX: number;
  offsetY: number;
  isDragging: boolean;
  lastMouseX: number;
  lastMouseY: number;
}

const CURVE_COLORS = [
  '#2196F3', '#F44336', '#4CAF50', '#FF9800', '#9C27B0',
  '#00BCD4', '#FFEB3B', '#795548', '#607D8B', '#E91E63',
  '#8BC34A', '#FF5722', '#3F51B5', '#009688', '#FFC107',
  '#673AB7', '#CDDC39', '#FF6F00', '#1976D2', '#388E3C',
];

/** Baseline zoom: values below 1 draw the trace smaller than the plot so the full curve fits without panning. Reset uses the same value. */
const INITIAL_VIEW_SCALE = 0.76;

const CurvesVisualizationModal: React.FC<CurvesVisualizationModalProps> = ({
  open,
  onClose,
  profile
}) => {
  const { colors } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  /** 0 = all curves overlaid; 1…N = single reference curve */
  const [slideIndex, setSlideIndex] = useState(0);
  const [canvasState, setCanvasState] = useState<CanvasState>({
    scale: INITIAL_VIEW_SCALE,
    offsetX: 0,
    offsetY: 0,
    isDragging: false,
    lastMouseX: 0,
    lastMouseY: 0
  });

  const curveCount = profile?.referenceCurves?.length ?? 0;
  const maxSlideIndex = curveCount;

  useEffect(() => {
    setSlideIndex((i) => Math.min(i, maxSlideIndex));
  }, [maxSlideIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      if (e.key === 'ArrowLeft') {
        setSlideIndex((i) => Math.max(0, i - 1));
      } else {
        setSlideIndex((i) => Math.min(maxSlideIndex, i + 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, maxSlideIndex]);

  const drawCurves = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !profile?.referenceCurves) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Setup canvas dimensions
    const width = canvas.width;
    const height = canvas.height;
    const padding = 60;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    // Draw background
    ctx.fillStyle = '#0a0a0a'; // Dark background
    ctx.fillRect(0, 0, width, height);

    // Draw plot area
    ctx.fillStyle = '#111111'; // Slightly lighter dark background
    ctx.fillRect(padding, padding, plotWidth, plotHeight);
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 1;
    ctx.strokeRect(padding, padding, plotWidth, plotHeight);

    const rawSelection =
      slideIndex === 0
        ? profile.referenceCurves
        : profile.referenceCurves[slideIndex - 1]
          ? [profile.referenceCurves[slideIndex - 1]]
          : [];

    const curvesToDraw = rawSelection
      .map((c) => ({
        ...c,
        signature: normalizeSignatureData(c.signature),
      }))
      .filter((c) => c.signature.length > 0);

    if (curvesToDraw.length === 0) return;

    // Find data bounds
    let minValue = Infinity;
    let maxValue = -Infinity;
    let maxLength = 0;

    curvesToDraw.forEach((curve) => {
      maxLength = Math.max(maxLength, curve.signature.length);
      curve.signature.forEach((value) => {
        minValue = Math.min(minValue, value);
        maxValue = Math.max(maxValue, value);
      });
    });

    if (!Number.isFinite(minValue) || !Number.isFinite(maxValue) || maxLength < 1) return;

    // Add some padding to the data range (flat line → still visible)
    let range = maxValue - minValue;
    if (!Number.isFinite(range) || range === 0) {
      minValue -= 1;
      maxValue += 1;
      range = 2;
    }
    const padding_value = range * 0.1;
    minValue -= padding_value;
    maxValue += padding_value;

    // Apply zoom and pan
    const scaleX = (plotWidth * canvasState.scale) / maxLength;
    const scaleY = (plotHeight * canvasState.scale) / (maxValue - minValue);

    // Draw grid lines
    ctx.strokeStyle = '#222222';
    ctx.lineWidth = 0.5;

    // Vertical grid lines
    const gridStepX = Math.max(10, Math.floor(maxLength / 10));
    for (let i = 0; i <= maxLength; i += gridStepX) {
      const x = padding + (i * scaleX) + canvasState.offsetX;
      if (x >= padding && x <= width - padding) {
        ctx.beginPath();
        ctx.moveTo(x, padding);
        ctx.lineTo(x, height - padding);
        ctx.stroke();
      }
    }

    // Horizontal grid lines
    const gridStepY = (maxValue - minValue) / 8;
    for (let i = 0; i <= 8; i++) {
      const value = minValue + (i * gridStepY);
      const y = height - padding - ((value - minValue) * scaleY) + canvasState.offsetY;
      if (y >= padding && y <= height - padding) {
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(width - padding, y);
        ctx.stroke();
      }
    }

    // Draw curves
    curvesToDraw.forEach((curve, curveIndex) => {
      const color = CURVE_COLORS[curveIndex % CURVE_COLORS.length];
      ctx.strokeStyle = color;
      ctx.lineWidth = slideIndex === 0 ? 2 : 3;

      ctx.beginPath();
      let isFirstPoint = true;

      curve.signature.forEach((value, index) => {
        const x = padding + (index * scaleX) + canvasState.offsetX;
        const y = height - padding - ((value - minValue) * scaleY) + canvasState.offsetY;

        if (isFirstPoint) {
          ctx.moveTo(x, y);
          isFirstPoint = false;
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.stroke();

      // Draw curve label if showing single curve
      if (slideIndex !== 0) {
        ctx.fillStyle = color;
        ctx.font = 'bold 14px Arial';
        ctx.fillText(`Curve: ${curve.id}`, padding + 10, padding + 20 + (curveIndex * 20));
      }
    });

    // Draw axes labels
    ctx.fillStyle = '#aaaaaa';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';

    // X-axis label (keep clear of bottom edge when canvas is CSS-scaled)
    ctx.fillText('Sample Index', width / 2, height - 26);

    // Y-axis label
    ctx.save();
    ctx.translate(20, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Sensor Value', 0, 0);
    ctx.restore();

    // Draw scale indicators
    ctx.textAlign = 'left';
    ctx.fillStyle = '#888888';
    ctx.font = '10px Arial';

    // Show zoom level
    ctx.fillText(`Zoom: ${(canvasState.scale * 100).toFixed(0)}%`, padding + 10, height - padding + 15);

    // Show data info
    ctx.fillText(`Points: ${maxLength} | Range: ${minValue.toFixed(2)} to ${maxValue.toFixed(2)}`,
      width - 200, height - padding + 15);
  }, [profile, slideIndex, canvasState]);

  /** Dialog mounts the canvas in a portal; the first paint often runs before ref attaches. Layout effect + rAF fixes that. */
  useLayoutEffect(() => {
    if (!open || !profile?.referenceCurves?.length) return;

    let cancelled = false;
    let attempts = 0;
    let rafId = 0;

    const paint = () => {
      if (cancelled) return;
      if (canvasRef.current) {
        drawCurves();
        return;
      }
      attempts += 1;
      if (attempts < 32) {
        rafId = requestAnimationFrame(paint);
      }
    };

    paint();
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [open, profile, slideIndex, canvasState.scale, canvasState.offsetX, canvasState.offsetY, drawCurves]);

  const handleZoomIn = () => {
    setCanvasState(prev => ({ ...prev, scale: Math.min(prev.scale * 1.2, 5) }));
  };

  const handleZoomOut = () => {
    setCanvasState(prev => ({ ...prev, scale: Math.max(prev.scale / 1.2, 0.1) }));
  };

  const handleReset = () => {
    setCanvasState({
      scale: INITIAL_VIEW_SCALE,
      offsetX: 0,
      offsetY: 0,
      isDragging: false,
      lastMouseX: 0,
      lastMouseY: 0
    });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    setCanvasState(prev => ({
      ...prev,
      isDragging: true,
      lastMouseX: e.clientX - rect.left,
      lastMouseY: e.clientY - rect.top
    }));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasState.isDragging) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    const deltaX = currentX - canvasState.lastMouseX;
    const deltaY = currentY - canvasState.lastMouseY;

    setCanvasState(prev => ({
      ...prev,
      offsetX: prev.offsetX + deltaX,
      offsetY: prev.offsetY + deltaY,
      lastMouseX: currentX,
      lastMouseY: currentY
    }));
  };

  const handleMouseUp = () => {
    setCanvasState(prev => ({ ...prev, isDragging: false }));
  };

  const handleSaveImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Create download link
    const link = document.createElement('a');
    link.download = `${profile?.name || 'profile'}_curves.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  if (!profile) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderTop: '1px solid rgba(34, 211, 238, 0.5)',
          borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          color: colors.TextPrimary,
          maxHeight: 'calc(100vh - 24px)',
          display: 'flex',
          flexDirection: 'column',
          my: 1,
        }
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "'Inter', sans-serif",
          color: '#ffffff',
          fontSize: '15px',
          fontWeight: 700,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          padding: '20px 24px',
          borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
          backgroundColor: 'transparent',
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ textTransform: 'none', letterSpacing: 'normal', fontSize: '1.25rem' }}>
              FlowMatch Reference Curves
            </Typography>
            <Typography variant="body2" sx={{ color: colors.TextSecondary }}>
              {profile.name} - {profile.referenceCurves?.length || 0} curves
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: 'transparent',
          padding: '24px',
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Controls */}
          <Paper sx={{
            p: 2,
            mb: 2,
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            boxShadow: 'none'
          }}>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flex: { xs: '1 1 100%', sm: '1 1 360px' },
                  minWidth: 0,
                  py: 0.75,
                  px: 1.5,
                  borderRadius: '14px',
                  border: '1px solid rgba(34, 211, 238, 0.2)',
                  background: 'rgba(0, 0, 0, 0.35)',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setSlideIndex((i) => Math.max(0, i - 1))}
                  disabled={slideIndex <= 0 || curveCount === 0}
                  aria-label="Previous curve"
                  sx={{ color: colors.Info, '&:disabled': { color: 'rgba(255,255,255,0.2)' } }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: colors.TextSecondary, display: 'block', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Curve view
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.TextPrimary, fontWeight: 700 }} noWrap title={slideIndex === 0 ? `All curves (${curveCount})` : `Curve ${slideIndex} of ${curveCount}`}>
                    {curveCount === 0
                      ? 'No curves'
                      : slideIndex === 0
                        ? `All curves (${curveCount})`
                        : `Curve ${slideIndex} / ${curveCount}`}
                  </Typography>
                  {slideIndex > 0 && curveCount > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ color: colors.TextMuted, display: 'block' }}
                      noWrap
                      title={profile.referenceCurves[slideIndex - 1]?.id}
                    >
                      {profile.referenceCurves[slideIndex - 1]?.id}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setSlideIndex((i) => Math.min(maxSlideIndex, i + 1))}
                  disabled={slideIndex >= maxSlideIndex || curveCount === 0}
                  aria-label="Next curve"
                  sx={{ color: colors.Info, '&:disabled': { color: 'rgba(255,255,255,0.2)' } }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ZoomInIcon />}
                  onClick={handleZoomIn}
                  disabled={canvasState.scale >= 5}
                  sx={{ color: colors.TextSecondary, borderColor: 'rgba(255, 255, 255, 0.2)', '&:hover': { borderColor: colors.TextPrimary, color: colors.TextPrimary } }}
                >
                  Zoom In
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ZoomOutIcon />}
                  onClick={handleZoomOut}
                  disabled={canvasState.scale <= 0.1}
                  sx={{ color: colors.TextSecondary, borderColor: 'rgba(255, 255, 255, 0.2)', '&:hover': { borderColor: colors.TextPrimary, color: colors.TextPrimary } }}
                >
                  Zoom Out
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestoreIcon />}
                  onClick={handleReset}
                  sx={{ color: colors.TextSecondary, borderColor: 'rgba(255, 255, 255, 0.2)', '&:hover': { borderColor: colors.TextPrimary, color: colors.TextPrimary } }}
                >
                  Reset View
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SaveAltIcon />}
                  onClick={handleSaveImage}
                  sx={{ color: colors.Info, borderColor: 'rgba(34, 211, 238, 0.5)', '&:hover': { borderColor: colors.Info, bgcolor: 'rgba(34, 211, 238, 0.1)' } }}
                >
                  Save PNG
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Canvas */}
          <Card sx={{
            background: 'rgba(255, 255, 255, 0.02)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            boxShadow: 'none'
          }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  width: '100%',
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                }}
              >
                <canvas
                  ref={canvasRef}
                  width={1000}
                  height={640}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: 'min(65vh, calc(100vh - 280px))',
                    display: 'block',
                    cursor: canvasState.isDragging ? 'grabbing' : 'grab',
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
              </Box>
              <Box mt={2}>
                <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(34, 211, 238, 0.05)', color: colors.Info, border: '1px solid rgba(34, 211, 238, 0.2)' }}>
                  💡 <strong>Controls:</strong> Click and drag to pan • Use zoom buttons to scale •
                  Use the carousel (or ← → keys) to step through curves or view all overlaid
                </Alert>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: colors.TextPrimary }}>Curve Statistics</Typography>
                    <Typography variant="body2" sx={{ color: colors.TextSecondary }}>
                      • Total Curves: {profile.referenceCurves?.length || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.TextSecondary }}>
                      • Average Length: {profile.referenceCurves?.length ?
                        Math.round(profile.referenceCurves.reduce((sum, curve) => sum + curve.signature.length, 0) / profile.referenceCurves.length) : 0} samples
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.TextSecondary }}>
                      • Category: {profile.category}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: colors.TextPrimary }}>Legend</Typography>
                    <Box
                      display="flex"
                      gap={1}
                      flexWrap="wrap"
                      alignItems="center"
                      sx={{
                        maxHeight: 140,
                        overflowY: 'auto',
                        pr: 0.5,
                      }}
                    >
                      {slideIndex === 0 && profile.referenceCurves?.map((curve, index) => (
                        <Chip
                          key={`${curve.id}-${index}`}
                          label={`Curve ${index + 1}`}
                          size="small"
                          sx={{
                            backgroundColor: `${CURVE_COLORS[index % CURVE_COLORS.length]}33`, // Transparent version of color
                            color: CURVE_COLORS[index % CURVE_COLORS.length],
                            border: `1px solid ${CURVE_COLORS[index % CURVE_COLORS.length]}`,
                            fontSize: '0.75rem'
                          }}
                        />
                      ))}
                      {slideIndex > 0 && (
                        <Chip
                          label={`Active · Curve ${slideIndex}`}
                          size="small"
                          sx={{
                            backgroundColor: `${CURVE_COLORS[(slideIndex - 1) % CURVE_COLORS.length]}33`,
                            color: CURVE_COLORS[(slideIndex - 1) % CURVE_COLORS.length],
                            border: `1px solid ${CURVE_COLORS[(slideIndex - 1) % CURVE_COLORS.length]}`,
                            fontSize: '0.75rem'
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent', flexShrink: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(34, 211, 238, 0.5)',
            background: 'rgba(34, 211, 238, 0.15)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CurvesVisualizationModal;